import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useConnectionStore } from '../../stores/connectionStore';
import { useCobrowse, dockSupportChat } from '../../hooks/useCobrowse';
import SupportSessionIcon from '../common/SupportSessionIcon';
import { supportSessionLabel } from '../common/supportSession';
import './BottomToolbar.css';

/**
 * The status strip.
 *
 * It used to be three things at once: a second navigation bar (System Log,
 * Selective Touch, Kitchen Display), a status readout (connection, version),
 * and a support tray (chat, cobrowse). Kitchen Display was already in the
 * sidebar, so it was a duplicate; the other two had no sidebar entry at all,
 * which is the only reason navigation was down here.
 *
 * All three are now sidebar items, and this strip reports state and offers
 * help — which is what a strip pinned to the bottom of every screen is for.
 * Nothing here changes what you are looking at.
 */
export default function BottomToolbar() {
  const navigate = useNavigate();
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);
  const [connAnchor, setConnAnchor] = useState<HTMLElement | null>(null);

  const facilityName = selectedConnection?.facilityName || '';
  const facilityCityStateZip = selectedConnection?.facilityCityStateZip || '';

  return (
    <footer className="bottom-toolbar" data-tour="bottom-toolbar">
      {/* Left: nothing navigational. The connection this session is bound to
          is the single most useful thing to state permanently — it is what
          every number on every screen is scoped to. */}
      <div className="bottom-toolbar-left" />

      {/* Right Section */}
      <div className="bottom-toolbar-right">
        {/* Connection Indicator */}
        <div
          className="bottom-toolbar-connection"
          onClick={(e) => setConnAnchor(e.currentTarget)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setConnAnchor(e.currentTarget as HTMLElement); } }}
          role="button"
          tabIndex={0}
          aria-label={`Current connection: ${facilityName || 'No Connection'}`}
          aria-haspopup="true"
        >
          <i className="bi bi-database-fill" aria-hidden="true" />
          <span className="bottom-toolbar-label">{facilityName || 'No Connection'}</span>
          <i className="bi bi-chevron-up" style={{ fontSize: 9 }} />
        </div>

        {/* Connection Popup */}
        <Popover
          open={Boolean(connAnchor)}
          anchorEl={connAnchor}
          onClose={() => setConnAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: { borderRadius: 2, overflow: 'hidden', minWidth: 280 },
            },
          }}
        >
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <i className="bi bi-building" style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }} />
              <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Current Connection</span>
            </div>

            {selectedConnection ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, marginBottom: 2 }}>
                  {facilityName}
                </div>
                {facilityCityStateZip && (
                  <div style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-muted)' }}>
                    {facilityCityStateZip}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-muted)', marginBottom: 12 }}>
                No connection selected
              </div>
            )}

            <div style={{ height: 1, background: 'var(--border-color)', marginBottom: 12 }} />

            <div
              className="bottom-toolbar-item"
              onClick={() => {
                setConnAnchor(null);
                navigate('/connection?switch=true');
              }}
              style={{
                padding: '8px 12px',
                fontSize: 'var(--font-size-base)',
              }}
            >
              <i className="bi bi-arrow-left-right" style={{ fontSize: 'var(--font-size-base)' }} />
              <span>Switch Connection</span>
            </div>
          </div>
        </Popover>

        {/* No help link here: the top bar's Help button is on every page and opens that page's
            help (the Knowledge Base is linked from the Help page). This strip had a Knowledge Base
            link to the support site until 2026-10-26 (Jeff: remove it). */}

        {/* Cobrowse Support Button */}
        <CobrowseSupportItem />

        {/* Version */}
        <Tooltip title="Dietech Web Application">
          <span className="bottom-toolbar-version">
            v{import.meta.env.VITE_APP_VERSION || 'dev'}
          </span>
        </Tooltip>
      </div>
    </footer>
  );
}

/**
 * Starts a PrimeManager cobrowse support session, or brings back its chat.
 *
 * This used to call the Zoho Assist widget (`window.openZohoCobrowse`), which
 * never worked against dietechonline.com. It now drives the in-house relay at
 * `cobrowse.dietechonline.com` — the same session the top toolbar's icon
 * controls, since `useCobrowse` keeps one SDK instance per page.
 */
function CobrowseSupportItem() {
  // The relay authenticates agents, not customers — the customer side connects
  // anonymously, so this component needs no OIDC context of its own.
  const noToken = useCallback(async () => null, []);
  const { status, sessionCode, chatOpen, chatUnread, startSession, openChat, minimizeChat } = useCobrowse(noToken);
  const isActive = status === 'active';
  const isBusy = status === 'connecting' || status === 'requesting-consent';
  const itemRef = useRef<HTMLDivElement>(null);

  // This button is where the chat docks: minimised, it leaves the page and
  // this button stands for it. Only while the button is actually on screen —
  // with nothing visible to bring it back, a minimised chat would be lost.
  useEffect(() => {
    const el = itemRef.current;
    if (!el) return undefined;
    const update = () => {
      if (!el.getClientRects().length) {
        dockSupportChat('strip', null);
        return;
      }
      const strip = el.closest('footer') ?? el;
      dockSupportChat('strip', { bottom: Math.max(0, window.innerHeight - strip.getBoundingClientRect().top) });
    };
    update();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      dockSupportChat('strip', null);
    };
  }, []);

  const handleActivate = () => {
    if (isBusy) return;
    // While a session runs this shows or minimises the chat; ending is in the chat.
    if (isActive) {
      if (chatOpen) minimizeChat();
      else openChat();
    } else startSession();
  };

  const unread = isActive && !chatOpen ? chatUnread : 0;
  const label = supportSessionLabel(status, sessionCode, chatOpen);
  const name = unread ? `${label}, ${unread} new ${unread === 1 ? 'message' : 'messages'}` : label;

  return (
    <div
      ref={itemRef}
      className="bottom-toolbar-item bottom-toolbar-support bottom-toolbar-session"
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
      role="button"
      tabIndex={0}
      // The visible label, so speech input ("click Start Support Session")
      // and the screen reader name match what is on screen. Kept as an
      // attribute because phones hide the label and show only the icon.
      aria-label={name}
      aria-busy={isBusy}
      aria-pressed={isActive ? chatOpen : undefined}
    >
      <SupportSessionIcon active={isActive} />
      <span className="bottom-toolbar-label">{label}</span>
      {unread > 0 && (
        <span className="bottom-toolbar-unread" aria-hidden="true">{unread > 9 ? '9+' : unread}</span>
      )}
    </div>
  );
}
